import { useCallback, useEffect, useRef, useState } from 'react';

import {
  getStrategicQuestions,
  StrategicQuestionsResponse,
} from 'services/actions/ai_management_strategic_questions';

// Enquanto a API gera (pendente) ou completa respostas faltantes (parcial).
export const STRATEGIC_QUESTIONS_POLL_DELAYS = [3000, 5000, 10000, 15000];
export const STRATEGIC_QUESTIONS_POLL_LIMIT = 10 * 60 * 1000;

interface State {
  biddingId: number | null;
  data: StrategicQuestionsResponse | null;
  loading: boolean;
  error: boolean;
  timedOut: boolean;
}

const EMPTY: State = { biddingId: null, data: null, loading: false, error: false, timedOut: false };

const isHidden = () => typeof document !== 'undefined' && document.hidden;

const needsPolling = (data: StrategicQuestionsResponse | null) =>
  Boolean(data && (data.pendente || data.parcial));

const statusOf = (err: unknown) =>
  (err as { response?: { status?: number } })?.response?.status ?? 'sem resposta';

export function usePerguntasEstrategicas(biddingId: number | null) {
  const [state, setState] = useState<State>(EMPTY);
  const [hidden, setHidden] = useState(isHidden);

  // Cada consulta recebe um número; só a mais recente (e com o componente montado) grava o estado.
  const requestRef = useRef(0);
  const mountedRef = useRef(true);
  const pollRef = useRef({ attempt: 0, startedAt: 0 });
  // Voltou à aba com consulta pendente: a próxima sai na hora, sem esperar o backoff.
  const resumeRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const load = useCallback((id: number, showLoading: boolean) => {
    requestRef.current += 1;
    const request = requestRef.current;
    const isCurrent = () => mountedRef.current && requestRef.current === request;

    if (showLoading) setState({ ...EMPTY, biddingId: id, loading: true });

    getStrategicQuestions(id)
      .then((response) => {
        if (!isCurrent()) return;
        setState({ ...EMPTY, biddingId: id, data: response.data });
      })
      .catch((err) => {
        console.error(
          `Erro ao buscar as perguntas estratégicas da licitacao ${id} (status ${statusOf(err)})`,
        );
        if (!isCurrent()) return;
        // Mantém o último resultado: uma falha no meio do polling não apaga o que já está na tela,
        // e a próxima consulta agendada tenta de novo.
        setState((prev) => ({ ...prev, biddingId: id, loading: false, error: true }));
      });
  }, []);

  const start = useCallback(
    (id: number) => {
      pollRef.current = { attempt: 0, startedAt: Date.now() };
      resumeRef.current = false;
      load(id, true);
    },
    [load],
  );

  useEffect(() => {
    if (!biddingId) {
      requestRef.current += 1;
      setState(EMPTY);
      return;
    }

    start(biddingId);
  }, [biddingId, start]);

  useEffect(() => {
    const onVisibilityChange = () => {
      const nowHidden = isHidden();
      if (!nowHidden) resumeRef.current = true;
      setHidden(nowHidden);
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);

  useEffect(() => {
    const id = state.biddingId;
    if (!id || id !== biddingId || hidden || state.timedOut || !needsPolling(state.data)) {
      return undefined;
    }

    if (Date.now() - pollRef.current.startedAt > STRATEGIC_QUESTIONS_POLL_LIMIT) {
      setState((prev) => ({ ...prev, timedOut: true }));
      return undefined;
    }

    const { attempt } = pollRef.current;
    const delay = resumeRef.current
      ? 0
      : STRATEGIC_QUESTIONS_POLL_DELAYS[
          Math.min(attempt, STRATEGIC_QUESTIONS_POLL_DELAYS.length - 1)
        ];
    const timer = window.setTimeout(() => {
      resumeRef.current = false;
      pollRef.current.attempt = attempt + 1;
      load(id, false);
    }, delay);

    // Cada resposta gera um novo `state`, que reagenda (ou encerra) a próxima consulta.
    return () => window.clearTimeout(timer);
  }, [state, biddingId, hidden, load]);

  const retry = useCallback(() => {
    if (biddingId) start(biddingId);
  }, [biddingId, start]);

  const current = state.biddingId === biddingId ? state : null;

  return {
    data: current?.data ?? null,
    loading: current ? current.loading : Boolean(biddingId),
    error: current?.error ?? false,
    timedOut: current?.timedOut ?? false,
    retry,
  };
}
